/**
 * 解析式「本地包围盒」—— 元素在**自身用户坐标系**里的框（与 getBBox() 同一坐标系）。
 *
 * 为什么自己算而不用 getBBox()：
 *  · jsdom / node 里根本没有 getBBox() → 分组、形状替换就全都无法单测；
 *  · 圆 / 椭圆 / 矩形 / 直线 / 折线 / 多边形的框是纯解析式，算出来逐位精确，没有理由依赖布局。
 *
 * `path` 是唯一的例外（要完整路径解析器才能算，见 core/shapes 的取舍）：
 * 有 getBBox() 就用（真浏览器），否则返回 null → UI 上标「无法变换」，绝不猜。
 *
 * ⚠️ 这里算的是**变换前**的框（元素自己的坐标系）。换形状时我们原样保留 transform，
 * 所以新形状也必须在同一个本地坐标系里生成 —— 旋转 / 嵌套组因此天然正确。
 */
import type { Pt, Rect } from './matrix';

/** 可以换形状的标签：叶子几何。文本 / 图片 / 组 / use 一律不在内（它们没法「换个形状」） */
export const CONVERTIBLE_TAGS = ['rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path'] as const;
export type ConvertibleTag = (typeof CONVERTIBLE_TAGS)[number];

export const isConvertibleTag = (tag: string): tag is ConvertibleTag =>
  (CONVERTIBLE_TAGS as readonly string[]).includes(tag);

const num = (v: string | null): number | null => {
  if (v === null || v.trim() === '') return null;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** points="x,y x,y …" → 点列表（逗号 / 空白 / 混用都认） */
export function parsePoints(raw: string | null): Pt[] {
  if (!raw) return [];
  const nums = raw
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  const out: Pt[] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) out.push({ x: nums[i] as number, y: nums[i + 1] as number });
  return out;
}

const boxOfPoints = (pts: readonly Pt[]): Rect | null => {
  if (pts.length === 0) return null;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
};

/**
 * 元素在自身坐标系里的包围盒。
 * 返回 null 表示「算不出来」（不支持的标签、缺几何属性、path 在无布局环境）→ 调用方跳过它。
 * ⚠️ 退化元素（水平 / 垂直直线的 h 或 w 为 0）会照实返回 0，由调用方决定要不要跳过。
 */
export function localBBox(el: Element): Rect | null {
  const tag = el.tagName.toLowerCase();
  const get = (name: string): number | null => num(el.getAttribute(name));

  switch (tag) {
    case 'rect': {
      const x = get('x') ?? 0;
      const y = get('y') ?? 0;
      const w = get('width');
      const h = get('height');
      if (w === null || h === null) return null;
      return { x, y, w, h };
    }
    case 'circle': {
      const cx = get('cx') ?? 0;
      const cy = get('cy') ?? 0;
      const r = get('r');
      if (r === null) return null;
      return { x: cx - r, y: cy - r, w: r * 2, h: r * 2 };
    }
    case 'ellipse': {
      const cx = get('cx') ?? 0;
      const cy = get('cy') ?? 0;
      const rx = get('rx');
      const ry = get('ry');
      if (rx === null || ry === null) return null;
      return { x: cx - rx, y: cy - ry, w: rx * 2, h: ry * 2 };
    }
    case 'line': {
      const x1 = get('x1');
      const y1 = get('y1');
      const x2 = get('x2');
      const y2 = get('y2');
      if (x1 === null || y1 === null || x2 === null || y2 === null) return null;
      return { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
    }
    case 'polyline':
    case 'polygon':
      return boxOfPoints(parsePoints(el.getAttribute('points')));
    case 'path': {
      // 路径要完整解析器才能算框 —— 有真实布局就用它的，没有就老实返回 null
      try {
        const b = (el as SVGGraphicsElement).getBBox?.();
        if (b && Number.isFinite(b.width) && Number.isFinite(b.height)) {
          return { x: b.x, y: b.y, w: b.width, h: b.height };
        }
      } catch {
        /* jsdom / 未渲染 */
      }
      return null;
    }
    default:
      return null;
  }
}

/** 多边形（含折线）的鞋带面积；点不足 3 个返回 0 */
export function polygonArea(pts: readonly Pt[]): number {
  if (pts.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i] as Pt;
    const b = pts[(i + 1) % pts.length] as Pt;
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/**
 * 元素当前的面积（「保持面积」口径用）。
 * 算不出来（path / 退化元素）返回 null → 调用方退回「保持包围盒」。
 */
export function localArea(el: Element): number | null {
  const tag = el.tagName.toLowerCase();
  const get = (name: string): number | null => num(el.getAttribute(name));
  switch (tag) {
    case 'rect': {
      const w = get('width');
      const h = get('height');
      return w === null || h === null ? null : Math.abs(w * h);
    }
    case 'circle': {
      const r = get('r');
      return r === null ? null : Math.PI * r * r;
    }
    case 'ellipse': {
      const rx = get('rx');
      const ry = get('ry');
      return rx === null || ry === null ? null : Math.PI * Math.abs(rx * ry);
    }
    case 'polyline':
    case 'polygon': {
      const area = polygonArea(parsePoints(el.getAttribute('points')));
      return area > 0 ? area : null;
    }
    case 'line':
      return null; // 线没有面积
    default:
      return null;
  }
}
